<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';

// Reuses the same already-loaded per-column contact data as the board and
// the overview strip — no extra request, no new field. A follow-up is
// "today's" purely by comparing the existing next_follow_up_at value's date
// portion against the current date, client-side.
const props = defineProps({
  columns: { type: Object, required: true },
});

const { t } = useI18n();
const router = useRouter();

const isToday = value => {
  if (!value) return false;
  const date = new Date(value);
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};

const todaysFollowUps = computed(() => {
  const all = Object.values(props.columns).flatMap(
    column => column.contacts || []
  );
  return all
    .filter(contact => isToday(contact.custom_attributes?.next_follow_up_at))
    .sort(
      (a, b) =>
        new Date(a.custom_attributes.next_follow_up_at) -
        new Date(b.custom_attributes.next_follow_up_at)
    );
});

const formatTime = value =>
  new Date(value).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });

const openContact = contactId => {
  router.push({ name: 'contacts_edit', params: { contactId } });
};
</script>

<template>
  <div class="flex flex-col flex-shrink-0 gap-1 mb-2">
    <div class="flex items-center gap-1.5 px-1">
      <Icon icon="i-lucide-clock-4" class="size-3.5 text-n-slate-10" />
      <h2 class="text-xs font-semibold text-n-slate-12">
        {{ t('KANBAN.TODAY_FOLLOW_UPS.TITLE') }}
      </h2>
      <span
        v-if="todaysFollowUps.length"
        class="flex items-center justify-center h-4 px-1.5 text-[10px] font-semibold rounded-full text-n-slate-10 bg-n-alpha-2 min-w-4"
      >
        {{ todaysFollowUps.length }}
      </span>
    </div>

    <div
      v-if="todaysFollowUps.length"
      class="flex items-center flex-shrink-0 gap-2 overflow-x-auto"
    >
      <div
        v-for="contact in todaysFollowUps"
        :key="contact.id"
        class="flex items-center flex-shrink-0 h-[30px] gap-2 pl-2 pr-3 border rounded-full cursor-pointer bg-n-solid-2 border-n-weak hover:border-n-slate-6"
        @click="openContact(contact.id)"
      >
        <span class="text-xs font-bold text-n-blue-11 tabular-nums">
          {{ formatTime(contact.custom_attributes.next_follow_up_at) }}
        </span>
        <span
          class="text-xs font-semibold truncate text-n-slate-12 max-w-[130px]"
        >
          {{ contact.name }}
        </span>
        <Label
          v-if="contact.custom_attributes?.stage"
          :label="contact.custom_attributes.stage"
          color="iris"
          compact
        />
        <template v-if="contact.assignee">
          <Avatar
            :name="contact.assignee.name"
            :src="contact.assignee.thumbnail"
            :size="16"
          />
          <span class="text-[11px] text-n-slate-10 truncate">
            {{ contact.assignee.name }}
          </span>
        </template>
        <span
          v-if="contact.custom_attributes?.follow_up_note"
          class="text-[11px] italic truncate text-n-slate-9 max-w-[150px]"
        >
          {{
            t('KANBAN.TODAY_FOLLOW_UPS.NOTE_QUOTED', {
              note: contact.custom_attributes.follow_up_note,
            })
          }}
        </span>
        <button
          type="button"
          disabled
          class="flex items-center flex-shrink-0 gap-1 px-1.5 py-0.5 text-[9px] font-bold tracking-wide rounded cursor-not-allowed text-n-amber-11 bg-n-amber-3"
          @click.stop
        >
          <Icon icon="i-lucide-message-square" class="size-2.5" />
          {{ t('KANBAN.TODAY_FOLLOW_UPS.SOON') }}
        </button>
      </div>
    </div>

    <div
      v-else
      class="flex items-center gap-2 px-3 h-[30px] border border-dashed rounded-lg border-n-weak"
    >
      <Icon icon="i-lucide-calendar-check" class="size-3.5 text-n-slate-8" />
      <span class="text-xs text-n-slate-10">
        {{ t('KANBAN.TODAY_FOLLOW_UPS.EMPTY') }}
      </span>
    </div>
  </div>
</template>
